import React, { useEffect, useRef, useState } from 'react';
import { COUNTRY_DATA } from '../../../constants/countryData';
import './index.scss';
import { Divider } from '../../../common/Divider';
import { useTranslation } from 'react-i18next'

const CountryCodeDropdown = ({
    id,
    name,
    userCountryCode,
    setCountryCode,
    triggerCodeValidation = () => {},
    setError
}) => {
    const dropdownRef = useRef();
    const { i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl';
    const normalizeCode = (code) => {
        if (!code) return '';
        const trimmed = String(code).trim();
        return trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/^\+/, '')}`;
    };

    const DEFAULT_CODE = '+971';
    const DEFAULT_COUNTRY =
        COUNTRY_DATA.find(item => normalizeCode(item.code) === normalizeCode(DEFAULT_CODE)) ||
        COUNTRY_DATA[0];

    const [inputValue, setInputValue] = useState(DEFAULT_COUNTRY?.code || '');
    const [selectedItem, setSelectedItem] = useState(DEFAULT_COUNTRY || null);
    const [filteredItems, setFilteredItems] = useState(COUNTRY_DATA);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isTyping, setIsTyping] = useState(false);

    // Sync parent once on mount if no external value provided
    useEffect(() => {
        if (!userCountryCode && DEFAULT_COUNTRY) {
            setCountryCode(DEFAULT_COUNTRY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userCountryCode === undefined || userCountryCode === null) return;

        const incoming = normalizeCode(userCountryCode);
        if (!incoming) return;

        // Avoid resetting if already selected
        if (selectedItem && normalizeCode(selectedItem.code) === incoming) return;

        const foundInList = COUNTRY_DATA.find(
            c => normalizeCode(c.code) === incoming
        );

        if (foundInList) {
            setSelectedItem(foundInList);
            setInputValue(foundInList.code);
            setCountryCode(foundInList);
        } else {
           
            const custom = { code: incoming, svgPath: '' };
            setSelectedItem(custom);
            setInputValue(custom.code);
            setCountryCode(custom);
        }
    }, [userCountryCode]); 


    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const input = inputValue.trim();
        if (!isTyping || !input) {
            setFilteredItems(COUNTRY_DATA);
            return;
        }
        const norm = normalizeCode(input);
        const matches = COUNTRY_DATA.filter(item =>
            item.code.includes(input) || normalizeCode(item.code).startsWith(norm)
        );
        setFilteredItems(matches);
    }, [inputValue, isTyping]);

    const handleSelect = (item) => {
        if (!item) return;
        setSelectedItem(item);
        setInputValue(item.code);
        setCountryCode(item);
        setShowDropdown(false);
        setIsTyping(false);
    };
	

    const finalizeInputSelection = () => {
        const raw = inputValue.trim();
        if (!raw) return;

        const normalized = normalizeCode(raw);
        let found = COUNTRY_DATA.find(c => normalizeCode(c.code) === normalized);

        if (!found) {
            const digits = raw.replace(/\D/g, '');
            if (digits) {
                found = COUNTRY_DATA.find(c => normalizeCode(c.code) === `+${digits}`);
            }
        }

        if (found) {
            setSelectedItem(found);
            setInputValue(found.code);
            setCountryCode(found);
        } else {
            // Not in list: create a custom entry so parent gets the new code
            const digits = raw.replace(/\D/g, '');
            if (digits) {
                const custom = { code: `+${digits}`, svgPath: '' };
                setSelectedItem(custom);
                setInputValue(custom.code);
                setCountryCode(custom); 
            }
        }
    };



    return (
        <div className={`country-code-wrapper ${isRTL ? 'rtl' : ''}`} ref={dropdownRef}>
            <div className="form-item__field search-wrapper-country">
                <div className="input-with-flag">
                    {selectedItem && selectedItem.svgPath !== '' && (
                        <img
                            src={selectedItem.svgPath}
                            alt="flag"
                            className="flag-icon"
                        />
                    )}

                    <input
                        type="text"
                        id={id}
                        name={name}
                        autoComplete="off"
                        className="p-inputtext"
                        value={inputValue}
                        onFocus={() => {
                            setFilteredItems(COUNTRY_DATA);
                            setShowDropdown(true);
                            setError && setError(prev => ({ ...prev, [id]: '' }));
                            setIsTyping(false);
                        }}
                        onClick={() => {
                            setFilteredItems(COUNTRY_DATA);
                            setShowDropdown(true);
                            setIsTyping(false);
                        }}
                        onChange={(e) => {
                            const value = e.target.value;
                            setInputValue(value);
                            setIsTyping(true);
                            setShowDropdown(true);
                            // While typing we clear selection so blur logic can resolve final
                            setSelectedItem(null);
                            if (value.trim() === '') {
                                setFilteredItems(COUNTRY_DATA);
                                setIsTyping(false);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                finalizeInputSelection();
                                setShowDropdown(false);
                                if (typeof triggerCodeValidation === 'function') {
                                    triggerCodeValidation();
                                }
                            }
                        }}
                        onBlur={() => {
                            setTimeout(() => {
                                finalizeInputSelection();
                                if (typeof triggerCodeValidation === 'function') {
                                    triggerCodeValidation();
                                }
                                setShowDropdown(false);
                            }, 120);
                        }}
                    />
                    <button
                        type="button"
                        className="p-button p-button-icon-only"
                        aria-expanded={showDropdown}
                        onClick={() => {
                            setFilteredItems(COUNTRY_DATA);
                            setShowDropdown(prev => !prev);
                            setIsTyping(false);
                        }}
                    >
                        <i className="pi pi-chevron-down" />
                    </button>
                    <Divider wrapperClassName={'country-divider'} />
                </div>
            </div>

			{showDropdown && (
				<ul
					className={
						`p-autocomplete-panel dropdown-list-country` +
						((filteredItems && filteredItems.length === 0) ? ' is-empty' : '')
					}
				>
					{filteredItems && filteredItems.length > 0 ? (
						filteredItems.map((item, idx) => (
							<li
								key={item.code || idx}
								onMouseDown={() => handleSelect(item)}
							>
								{item.svgPath && <img src={item.svgPath} alt="flag" />}
								<span>{item.code}</span>
							</li>
						))
					) : (
						<li className="no-results">&nbsp;</li>
					)}
				</ul>
			)}
        </div>
    );
};

export default CountryCodeDropdown;
